import { useState, useEffect, useMemo } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {
  faSquarePollHorizontal,
  faCalendarRange,
  faAngleLeft,
  faMessageLines,
  faRocket,
  faPhone,
  faMessageDots,
} from '@fortawesome/pro-regular-svg-icons';
import {
  BoxV2 as Box,
  Flex,
  Table,
  useTable,
  Tooltip,
  RoleGuard,
} from 'portal-commons';
import {
  CarrierStatusSection,
  StatusSection,
  CampaignDetailsSection,
  LaunchDetailsSection,
  TechnicalDetailsSection,
  SampleMessagesSection,
  OtherResponsiblePartiesSection,
  RcsSectionBlock,
  BasicInfoSection,
  ComplianceAttributesForm,
  CampaignDetailsEditModal,
  LaunchDetailsForm,
  TechnicalContactForm,
  SampleMessagesForm,
  CampaignDetailsForm,
  MoreButton,
  ElectCnpAlert,
  ElectCnpDialog,
  CampaignActionModal,
  BrandDetailsSection,
  TenDlcCampaignFallbackSection,
} from '../components/RcsCampaignDetail';
import { RCS_CAMPAIGNS_PATH } from '@/constants/paths';
import EventListingRow from '@/components/EventListingRow';
import { Event } from '../../Events/types';
import { Loader } from '@/shared_elements';
import { RcsCampaign } from '../types';
import { toastFlashMessage } from '@/utils';
import { DeclineCnpRcsModal, ElectCnpRcsModal } from '../components';
import {
  campaignController,
  brandAssetController,
  eventController,
  BrandAsset,
  resellerController,
  CspCampaignSharing,
  BrandAssetStatus,
  enumController,
  BrandAssetType,
  Reseller,
  CampaignOperation,
  CampaignVerification,
  CampaignDetail,
} from '@/apis/core';
import {
  getRcsCnpListOptions,
  getRejectionCategoryOptions,
} from '@/apis/services';
import { SelectItem } from '@/utils/types';
import { NUDGE_TYPES } from '../constants';
import { MAX_EVENT_RECORDS } from '../../Events/constants';

export const EVENT_TABLE_HEADERS = [
  { id: 'eventType', label: 'Event Type', sortable: false },
  { id: 'categoryType', label: 'Category', sortable: false },
  { id: 'campaignUid', label: 'Campaign ID', sortable: false },
  { id: 'brandUid', label: 'Brand ID', sortable: false },
  { id: 'updateDate', label: 'Date & Time', sortable: false },
  { id: 'description', label: 'Description', sortable: false },
];

export enum EditModalType {
  None = 'None',
  CampaignDetails = 'CampaignDetails',
  LaunchDetails = 'LaunchDetails',
  TechnicalContact = 'TechnicalContact',
  SampleMessages = 'SampleMessages',
}

const EDIT_MODAL_CONFIG: {
  [key in EditModalType]: {
    icon: FontAwesomeIconProps['icon'];
    title: string;
  };
} = {
  [EditModalType.None]: {
    icon: faMessageLines,
    title: '',
  },
  [EditModalType.CampaignDetails]: {
    icon: faMessageLines,
    title: 'Campaign Details',
  },
  [EditModalType.LaunchDetails]: {
    icon: faRocket,
    title: 'Launch Details',
  },
  [EditModalType.TechnicalContact]: {
    icon: faPhone,
    title: 'Technical Contact',
  },
  [EditModalType.SampleMessages]: {
    icon: faMessageDots,
    title: 'Sample Messages',
  },
};

enum ActionModalType {
  None = 'None',
  ElectCnp = 'ElectCnp',
  NudgeCnpReview = 'NudgeCnpReview',
  NudgeCnpAppealRejection = 'NudgeCnpAppealRejection',
  Deactivate = 'DeactivateCampaign',
  DeclineElection = 'DeclineElection',
}

const RcsCampaignDetail = ({ csp }: { csp: boolean }) => {
  const location = useLocation<{
    goBackPage?: string;
    rcsFallback?: { fallbackCampaignUid: string };
  }>();
  const history = useHistory();
  const { id: campaignUid } = useParams<{ id: string }>();
  const [goBackPage] = useState(
    location.state?.goBackPage ?? RCS_CAMPAIGNS_PATH
  );
  const [campaign, setCampaign] = useState<CampaignDetail>();
  const [pageLoading, setPageLoading] = useState(true);
  const [openingEditModal, setOpeningEditModal] = useState<EditModalType>(
    EditModalType.None
  );
  const [isElectCNPAlertOpen, setIsElectCNPAlertOpen] = useState(false);
  const [campaignActionModal, setCampaignActionModal] =
    useState<ActionModalType>(ActionModalType.None);

  const [electCnpOptions, setElectCnpOptions] = useState<SelectItem[]>([]);
  const [rejectionCategories, setRejectionCategories] = useState<SelectItem[]>(
    []
  );
  const [electedDcas, setElectedDcas] = useState<number[]>([]);
  const [nominationInfo, setNominationInfo] = useState<CspCampaignSharing>();
  const [statusData, setStatusData] = useState<{
    rcsVet?: string | null;
    logo?: string | null;
    banner?: string | null;
  }>({});

  const [campaignOperations, setCampaignOperations] = useState<
    CampaignOperation[]
  >([]);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [activeAssets, setActiveAssets] = useState<{
    logo: BrandAsset | undefined;
    banner: BrandAsset | undefined;
  }>({
    logo: undefined,
    banner: undefined,
  });
  const [reseller, setReseller] = useState<Reseller>();
  const [isProcessing, setIsProcessing] = useState(false);
  const isExpired = campaign?.status === 'EXPIRED';
  const [isMoreBtnVisible, setIsMoreBtnVisible] = useState(csp);
  const [verificationRecords, setVerificationRecords] = useState<
    CampaignVerification[]
  >([]);

  const logoOptions = useMemo(() => {
    return assets.filter(
      (asset) =>
        asset.type === BrandAssetType.Logo &&
        (!asset.verificationStatus ||
          asset.verificationStatus === BrandAssetStatus.Pending ||
          asset.verificationStatus === BrandAssetStatus.Verified)
    );
  }, [assets]);

  const bannerOptions = useMemo(() => {
    return assets.filter(
      (asset) =>
        asset.type === BrandAssetType.Banner &&
        (!asset.verificationStatus ||
          asset.verificationStatus === BrandAssetStatus.Pending ||
          asset.verificationStatus === BrandAssetStatus.Verified)
    );
  }, [assets]);

  const carriers = useMemo(() => {
    return campaignOperations.map((operation) => {
      const result = operation.rulesEngineResultsJson as {
        mno: string;
        qualify: boolean;
        mnoReview: boolean;
        networkId: number;
      };
      return {
        provider: result.mno,
        qualify: result.qualify ? 'Yes' : 'No',
        mnoReview: result.mnoReview ? 'Yes' : 'No',
        billingCategory: 'All', // TODO: update until API is ready
        fallbackSupported: 'Yes', // TODO: update until API is ready
        status: operation.status,
        dca: electedDcas.includes(operation.networkId) ? 'Yes' : 'No',
        verification: verificationRecords?.find(
          (v) =>
            Array.isArray(v.mnoIds) && v.mnoIds.includes(operation.networkId)
        ),
      };
    });
  }, [electedDcas, campaignOperations, verificationRecords]);

  const fetchEventHistory = async (tableFilter: any) => {
    const params = {
      ...tableFilter,
      campaignUid,
      sortField: 'createDate',
      ascendingOrder: false,
    };
    const response = await eventController.getEventHistory(params);
    return {
      data: response?.records || [],
      total: response?.totalRecords || 0,
      rowsPerPage: response?.recordsPerPage || 10,
      page: response?.page || 1,
    };
  };

  const eventTable = useTable<Event>(fetchEventHistory, {
    deps: [campaignUid],
    inMemory: true,
    cappingTotal: MAX_EVENT_RECORDS,
  });

  const updateCnpOptions = async () => {
    const options = await getRcsCnpListOptions();
    setElectCnpOptions(options);
  };

  const fetchRejectionCategories = async (): Promise<void> => {
    const options = await getRejectionCategoryOptions();
    setRejectionCategories(options);
  };

  const campaignActionMenuOptions = useMemo(() => {
    if (csp) {
      return [
        {
          label: 'Nudge CNP to Review',
          value: ActionModalType.NudgeCnpReview,
        },
        {
          label: 'Nudge CNP to Appeal Rejection',
          value: ActionModalType.NudgeCnpAppealRejection,
        },
        {
          label: 'Deactivate',
          value: ActionModalType.Deactivate,
        },
      ];
    }
    return [
      { label: 'Accept', value: ActionModalType.ElectCnp },
      { label: 'Decline', value: ActionModalType.DeclineElection },
    ];
  }, [csp]);

  const fetchCampaignDetail = async (uid: string) => {
    const fullCampaign = await Promise.allSettled([
      campaignController.getCampaignDetail(uid),
      enumController.getUseCases({
        channelId: 'RCS',
      }),
      !csp ? campaignController.getRcsCnpCampaigns({ campaignUid: uid }) : null,
    ]).then(([detailResult, useCaseResult, listResult]) => {
      const cDetail =
        detailResult.status === 'fulfilled' ? detailResult.value : undefined;
      const cListItem =
        listResult?.status === 'fulfilled'
          ? listResult.value?.records[0]
          : undefined;
      const useCaseTypes =
        useCaseResult.status === 'fulfilled' ? useCaseResult.value : [];

      return {
        ...cDetail,
        /* details API does not return upstream and downstream info now but use them if they are returned in the future */
        downstreamCnpName:
          cDetail?.downstreamCnpName || cListItem?.downstreamCnpName,
        upstreamCnpName: cDetail?.upstreamCnpName || cListItem?.upstreamCnpName,
        sharingStatus: cListItem?.sharingStatus,
        usecase:
          useCaseTypes.find((useCase) => useCase.id === cDetail?.usecase)
            ?.displayName || cDetail?.usecase,
      };
    });

    setCampaign(fullCampaign);
    if (csp) {
      updateStatusSectionData(
        fullCampaign.brand?.logoStatus,
        fullCampaign.brand?.bannerStatus,
        fullCampaign.brand?.rcsVettingStatus === 'ACTIVE'
          ? 'VERIFIED'
          : fullCampaign.brand?.rcsVettingStatus
      );
    } else {
      setIsMoreBtnVisible(fullCampaign?.sharingStatus === 'PENDING');
    }
  };

  const fetchPageInitData = async (_campaignUid: string) => {
    await Promise.allSettled([
      fetchCampaignDetail(_campaignUid),
      updateElectedDca(_campaignUid),
      updateNominationStatus(_campaignUid),
      getCarrierStatusData(_campaignUid),
    ]);
  };

  useEffect(() => {
    updateCnpOptions();
    fetchRejectionCategories();
  }, []);

  useEffect(() => {
    if (location.state?.rcsFallback?.fallbackCampaignUid) {
      toastFlashMessage(
        `${location.state.rcsFallback.fallbackCampaignUid} successfully created and selected as fallback campaign`,
        'success '
      );
      // fix XP-1248, should not re-appear the above toast when user clicks browser back button to visit the page
      history.replace(`${location.pathname}${location.search}`, {
        ...location.state,
        rcsFallback: undefined,
      });
    }
  }, []);

  useEffect(() => {
    setPageLoading(true);
    (async () => {
      try {
        await fetchPageInitData(campaignUid);
      } finally {
        setPageLoading(false);
      }
    })();
    return () => {
      setCampaign(undefined);
      setElectedDcas([]);
      setNominationInfo(undefined);
      setCampaignOperations([]);
      setStatusData({});
      setActiveAssets({
        logo: undefined,
        banner: undefined,
      });
    };
  }, [campaignUid]);

  useEffect(() => {
    if (campaign?.brandUid) {
      getAssets(campaign.brandUid);
    }
  }, [campaign?.brandUid]);

  useEffect(() => {
    const logo = assets.find(
      (asset) =>
        asset.type === 'LOGO' && asset.attachmentUuid === campaign?.campaignLogo
    );
    const banner = assets.find(
      (asset) =>
        asset.type === 'BANNER' &&
        asset.attachmentUuid === campaign?.campaignBanner
    );
    setActiveAssets({ logo, banner });
    updateStatusSectionData(
      logo?.verificationStatus,
      banner?.verificationStatus
    );
  }, [assets, campaign?.campaignLogo, campaign?.campaignBanner]);

  useEffect(() => {
    if (campaign?.resellerUid) {
      fetchReseller(campaign.resellerUid);
    }
  }, [campaign?.resellerUid]);

  useEffect(() => {
    if (csp && !!campaignUid) {
      (async () => {
        const verification = await campaignController.getVerificationRecords(
          campaignUid,
          undefined,
          {
            silent: true,
          }
        );
        setVerificationRecords(verification);
      })();

      return () => {
        setVerificationRecords([]);
      };
    }
  }, [csp, campaignUid]);

  const handleComplianceAttributesUpdate = async (
    data: Partial<RcsCampaign>
  ) => {
    if (!campaign) return;

    const originalCampaign = { ...campaign };
    setCampaign({
      ...originalCampaign,
      ...data,
    });
    const response = await campaignController.updateCampaign(campaignUid, data);
    if (response) {
      toastFlashMessage('Campaign updated successfully', 'success');
    } else {
      setCampaign(originalCampaign);
    }
  };

  const nudgeCnpReview = async (description: string) => {
    setIsProcessing(true);
    const params = {
      intent: NUDGE_TYPES.REVIEW,
      description,
    };
    const response = await campaignController.nudgeUpstreamCnp(
      campaignUid,
      params
    );
    response && toastFlashMessage('Nudge successfully sent.', 'success');
    setCampaignActionModal(ActionModalType.None);
    setIsProcessing(false);
  };

  const nudgeCnpAppealRejection = async (description: string) => {
    setIsProcessing(true);
    const params = {
      intent: NUDGE_TYPES.APPEAL_REJECTION,
      description,
    };
    const response = await campaignController.nudgeUpstreamCnp(
      campaignUid,
      params
    );
    response && toastFlashMessage('Notification successfully sent.', 'success');
    setCampaignActionModal(ActionModalType.None);
    setIsProcessing(false);
  };

  const deactivateCampaign = async () => {
    setIsProcessing(true);
    const response = await campaignController.deactivateCampaign(campaignUid);
    if (response) {
      toastFlashMessage('Campaign deactivated successfully.', 'success');
      fetchPageInitData(campaignUid);
    }
    setCampaignActionModal(ActionModalType.None);
    setIsProcessing(false);
  };

  const handleCampaignTopLevelAction = async (
    type: string,
    data: { message?: string; cnp?: string }
  ) => {
    const { message = '' } = data;
    switch (type) {
      case 'review':
        await nudgeCnpReview(message);
        break;
      case 'appealRejection':
        await nudgeCnpAppealRejection(message);
        break;
      case 'deactivate':
        await deactivateCampaign();
        break;
      default:
        break;
    }
  };

  const getCarrierStatusData = async (campaignId: string) => {
    const operations = await campaignController.getCampaignOperations(
      campaignId
    );
    if (Array.isArray(operations)) {
      setCampaignOperations(operations);
    } else {
      setCampaignOperations([]);
    }
  };

  const getAssets = async (brandUid: string) => {
    const brandAssets = await brandAssetController.getBrandAssets(
      brandUid,
      {},
      true
    );
    setAssets(brandAssets);
  };

  const updateStatusSectionData = async (
    logo?: string | null,
    banner?: string | null,
    rcsVetStatus?: string | null
  ) => {
    setStatusData((prevData) => {
      return {
        rcsVet: rcsVetStatus ?? prevData.rcsVet,
        logo: logo ?? prevData.logo,
        banner: banner ?? prevData.banner,
      };
    });
  };

  const handleEditModalSave = async (
    data: Partial<RcsCampaign> & { logo?: BrandAsset; banner?: BrandAsset }
  ) => {
    if (!campaign) return;
    setIsProcessing(true);
    const { logo, banner, ...restCampaign } = data;
    const params = {
      ...restCampaign,
    };
    if (banner?.attachmentUuid) {
      params.campaignBanner = banner.attachmentUuid;
    }
    if (logo?.attachmentUuid) {
      params.campaignLogo = logo.attachmentUuid;
    }
    const response = await campaignController.updateCampaign(
      campaign.uid,
      params
    );
    if (response) {
      if (params.campaignBanner || params.campaignLogo) {
        getAssets(campaign.brandUid);
      }
      setCampaign((prev) => ({ ...(prev as RcsCampaign), ...params }));
      setOpeningEditModal(EditModalType.None);
      toastFlashMessage('Campaign updated successfully', 'success');

      if (response.resellerUid !== campaign.resellerUid) {
        fetchReseller(response.resellerUid);
      }
    } else {
      toastFlashMessage('Campaign update failed', 'error');
    }
    setIsProcessing(false);
  };

  const fetchReseller = async (resellerUid: string) => {
    const resellerInfo = await resellerController.getReseller(resellerUid);
    resellerInfo && setReseller(resellerInfo);
  };

  const updateNominationStatus = async (uid: string) => {
    const data = await campaignController.getCampaignSharingStatus(uid);
    if (data) {
      setNominationInfo(data);
      if (data.status) {
        setIsElectCNPAlertOpen(['DECLINED'].includes(data.status));
      } else {
        setIsElectCNPAlertOpen(true);
      }
    }
  };

  const updateElectedDca = async (uid: string) => {
    const data = await campaignController.getCampaignElectedDcas(uid);
    if (data) {
      setElectedDcas(data);
    }
  };

  if (pageLoading || !campaign) {
    return <Loader />;
  }

  return (
    <div data-testid="rcsCampaignDetail" style={{ position: 'relative' }}>
      <Flex
        as="a"
        sx={{ mb: 'xl', width: 'fit-content', cursor: 'pointer' }}
        onClick={() => history.push(goBackPage)}
        data-testid="goBackBtn"
      >
        <Box sx={{ mr: 'xs' }}>
          <FontAwesomeIcon icon={faAngleLeft} color="#68737A" />
        </Box>
        {goBackPage.startsWith(RCS_CAMPAIGNS_PATH)
          ? 'back to all campaigns'
          : 'back'}
      </Flex>
      {isMoreBtnVisible && (
        <RoleGuard feature="rcsCampaignDetail.viewMoreActions">
          <MoreButton
            containerSx={{
              right: '16px',
              top: '30px',
            }}
            disabled={isExpired}
            options={campaignActionMenuOptions}
            onSelect={(val: string) =>
              setCampaignActionModal(val as ActionModalType)
            }
            menuMinWidth={csp ? '200px' : undefined}
          />
          {csp && (
            <Tooltip
              style={{
                right: '0',
                top: '50px',
              }}
              placement="bottom"
              title={
                <div>
                  <div>
                    <b>Review</b>: Use this to remind your CNP to review the
                    campaign and/or take action.
                  </div>
                  <br />
                  <div>
                    <b>Appeal Rejection</b>: Use this after you have edited and
                    resubmitted a campaign to appeal the rejection by your
                    CNP/DCA.
                  </div>
                  <br />
                  <div>
                    <b>Deactivate</b>: This will inform the DCA to deactivate
                    the campaign on all MNOs.
                  </div>
                </div>
              }
            />
          )}
        </RoleGuard>
      )}
      <Box
        as="h1"
        sx={{
          fontSize: 'H600',
          fontWeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        RCS Campaign ID:
        <Box as="span" sx={{ fontWeight: 'semiBold', ml: 'xs' }}>
          {campaign?.uid}
        </Box>
        <BasicInfoSection
          campaign={campaign}
          type={csp ? 'csp' : 'cnp'}
          onUpdate={handleEditModalSave}
        />
      </Box>
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 'xl',
        }}
      >
        <CarrierStatusSection data={carriers} />
        {!csp && <BrandDetailsSection campaign={campaign} />}
        {csp && <StatusSection data={statusData} />}
        <RoleGuard
          feature="rcsCampaignDetail.editCampaignDetails"
          render={(authorized) => (
            <CampaignDetailsSection
              campaign={campaign}
              logo={activeAssets.logo}
              banner={activeAssets.banner}
              onEdit={() => setOpeningEditModal(EditModalType.CampaignDetails)}
              editable={csp && authorized && !isExpired}
            />
          )}
        />
        <RoleGuard
          feature="rcsCampaignDetail.editLaunchDetails"
          render={(authorized) => (
            <LaunchDetailsSection
              campaign={campaign}
              editable={csp && authorized && !isExpired}
              onEdit={() => setOpeningEditModal(EditModalType.LaunchDetails)}
            />
          )}
        />
        <RoleGuard
          feature="rcsCampaignDetail.editTechnicalContact"
          render={(authorized) => (
            <TechnicalDetailsSection
              campaign={campaign}
              editable={csp && authorized && !isExpired}
              onEdit={() => setOpeningEditModal(EditModalType.TechnicalContact)}
            />
          )}
        />
        <RoleGuard
          feature="rcsCampaignDetail.editSampleMessages"
          render={(authorized) => (
            <SampleMessagesSection
              editable={csp && authorized && !isExpired}
              messages={
                [
                  campaign.sample1,
                  campaign?.sample2,
                  campaign?.sample3,
                  campaign?.sample4,
                  campaign?.sample5,
                ].filter((msg) => !!msg) as string[]
              }
              onEdit={() => setOpeningEditModal(EditModalType.SampleMessages)}
            />
          )}
        />
        <RcsSectionBlock
          icon={faSquarePollHorizontal}
          title="Compliance Attributes"
          showDivider={csp}
          editable={false}
        >
          <RoleGuard
            feature="rcsCampaignDetail.editComplianceAttributes"
            render={(authorized) => (
              <ComplianceAttributesForm
                campaign={campaign}
                onUpdate={handleComplianceAttributesUpdate}
                editable={csp && authorized && !isExpired}
              />
            )}
          />
        </RcsSectionBlock>
        {csp && (
          <OtherResponsiblePartiesSection
            campaign={campaign}
            reseller={reseller}
            nominationInfo={nominationInfo}
            electedDcas={electedDcas}
            onElectCnpClick={() =>
              setCampaignActionModal(ActionModalType.ElectCnp)
            }
            onResellerChange={(resellerUid) => {
              setCampaign({
                ...campaign,
                resellerUid,
              });
            }}
          />
        )}
        {csp && (
          <TenDlcCampaignFallbackSection
            fallbackCampaignUid={campaign.fallbackCampaignUid}
            brandUid={campaign.brandUid}
            brandName={campaign.brandName}
            rcsCampaignUid={campaign.uid}
            disableActions={campaign.status !== 'ACTIVE'}
            onChange={(newFallbackCampaignUid: string | null) => {
              setCampaign({
                ...campaign,
                fallbackCampaignUid: newFallbackCampaignUid ?? undefined,
              });
            }}
          />
        )}
        <RcsSectionBlock
          icon={faCalendarRange}
          title="Events"
          showDivider={true}
          editable={false}
        >
          <Table
            testId="campaignDetailEventsTable"
            className="campaign-detail-events-table"
            headRows={EVENT_TABLE_HEADERS}
            emptyState="no events to view"
            disableHover
            loading={eventTable.loading}
            tableData={eventTable.tableData.map((record, index) => (
              <EventListingRow
                key={index}
                event={record}
                brandLink
                withoutUser
              />
            ))}
            handleChangePage={eventTable.handleChangePage}
            pagination={eventTable.pagination}
            headerTextTransform="none"
            emptyBlockHeight="180px"
          />
        </RcsSectionBlock>
      </Flex>
      <CampaignDetailsEditModal
        open={openingEditModal !== EditModalType.None}
        onCancel={() => setOpeningEditModal(EditModalType.None)}
        onSave={handleEditModalSave}
        title={EDIT_MODAL_CONFIG[openingEditModal].title}
        icon={EDIT_MODAL_CONFIG[openingEditModal].icon}
        disabled={isProcessing}
      >
        {openingEditModal === EditModalType.LaunchDetails && (
          <LaunchDetailsForm campaign={campaign} />
        )}
        {openingEditModal === EditModalType.TechnicalContact && (
          <TechnicalContactForm campaign={campaign} />
        )}
        {openingEditModal === EditModalType.SampleMessages && (
          <SampleMessagesForm campaign={campaign} />
        )}
        {openingEditModal === EditModalType.CampaignDetails && (
          <CampaignDetailsForm
            campaign={campaign}
            logo={activeAssets.logo!}
            banner={activeAssets.banner!}
            logoOptions={logoOptions}
            bannerOptions={bannerOptions}
            disallowEdit={verificationRecords?.length > 0}
          />
        )}
      </CampaignDetailsEditModal>
      {/* FIXME: use GET /sharedByMe/${campaignUid} to check if opens this alert */}
      <ElectCnpAlert
        open={isElectCNPAlertOpen}
        onClose={() => setIsElectCNPAlertOpen(false)}
      />
      {csp ? (
        <ElectCnpDialog
          open={campaignActionModal === ActionModalType.ElectCnp}
          options={electCnpOptions}
          campaignUid={campaign?.uid}
          onClose={(flag?: boolean) => {
            setCampaignActionModal(ActionModalType.None);
            if (flag) {
              updateNominationStatus(campaign.uid);
              updateElectedDca(campaign.uid);
            }
          }}
        />
      ) : (
        <ElectCnpRcsModal
          open={campaignActionModal === ActionModalType.ElectCnp}
          campaignUid={campaign?.uid}
          cnpOptions={electCnpOptions}
          onClose={(flag?: boolean) => {
            setCampaignActionModal(ActionModalType.None);
            if (flag) {
              updateNominationStatus(campaign.uid);
              updateElectedDca(campaign.uid);
            }
          }}
        />
      )}
      .
      <CampaignActionModal
        type={(() => {
          switch (campaignActionModal) {
            case ActionModalType.NudgeCnpReview:
              return 'review';
            case ActionModalType.NudgeCnpAppealRejection:
              return 'appealRejection';
            case ActionModalType.Deactivate:
              return 'deactivate';
            default:
              return 'deactivate';
          }
        })()}
        open={[
          ActionModalType.NudgeCnpReview,
          ActionModalType.NudgeCnpAppealRejection,
          ActionModalType.Deactivate,
        ].includes(campaignActionModal)}
        disabled={isProcessing}
        onCancel={() => setCampaignActionModal(ActionModalType.None)}
        onConfirm={handleCampaignTopLevelAction}
      />
      {campaignActionModal === ActionModalType.DeclineElection && (
        <DeclineCnpRcsModal
          open
          campaign={campaign}
          categories={rejectionCategories}
          onClose={() => setCampaignActionModal(ActionModalType.None)}
        />
      )}
    </div>
  );
};

export default RcsCampaignDetail;
